import { routesMap } from "./routesMap";
import { analytics } from "./GTM";
import Intercom from '@intercom/messenger-js-sdk';
// Import user helper functions
const { isUserApplicant, getUser } = require('./userHelpers');

export const initServices = () => {
  /**
   * Amplitude
   * https://www.docs.developers.amplitude.com/data/sdks/marketing-analytics-browser/
   *
   */
  analytics.init();
};

/**
 * Initialize Intercom with user data
 */
export const initIntercom = (userData = {}) => {
  if (!process.env.REACT_APP_INTERCOM_APP_ID) {
    return;
  }
  if (!isUserApplicant()) {
    return;
  }

  try {
    const user = getUser();
    if (!user) return;

    // Handle both old and new user data structures
    const email = user.email || userData.email;
    const userId = user.user_id || user.localId || userData.user_id;
    const name = user.first_name || user.firstName || user.displayName || userData.name;
    const companyName = user.company_name || user.companyName || userData.companyName;
    const emailVerified = user.email_verified || user.emailVerified;
    const createdAt = user.created_at || userData.created_at;

    if (email && userId) {
      Intercom({
        app_id: process.env.REACT_APP_INTERCOM_APP_ID,
        user_id: userId,
        name: name,
        email: email,
        created_at: createdAt,
        email_verified: emailVerified,
        company: companyName ? {
          name: companyName
        } : undefined
      });
    }
  } catch (error) {
    console.error('Error initializing Intercom:', error);
  }
};

/**
 * update Intercom on every page change
 */
export const updateIntercom = () => {
  try {
    // Only update Intercom for applicant users
    if (!isUserApplicant()) {
      return;
    }
    Intercom("update");
  } catch (error) {
    console.error('Error updating Intercom:', error);
  }
};


/**
 * This function is used to update the user data on Intercom
 * @param {*object} param0
 * @returns void
 *
 */
export const updateIntercomUserData = ({
  email,
  firstName,
  plan,
  audioUsed,
  audioTotal,
  audioWithMediaUsed,
  audioWithMediaTotal,
  companyName,
}) => {
  try {
    // Only update Intercom for applicant users
    if (!isUserApplicant()) {
      return;
    }
    const user = getUser();
    if (!user) return;

    const userId = user.user_id || user.localId;
    const emailVerified = user.email_verified || user.emailVerified;

    if (email && userId) {
      // Calculate audio usage left
      const audio_usage_left = audioTotal - audioUsed;
      const audio_with_media_usage_left =
        audioWithMediaTotal - audioWithMediaUsed;

      // Update user data on Intercom using the imported SDK
      Intercom("update", {
        user_id: userId,
        email,
        name: firstName,
        "Subscribed Plan": plan || "",
        plan: plan || "",
        "First Name": firstName || "",
        "Company name": companyName || "",
        "Audio Usage Left from Plan":
          audio_usage_left + audio_with_media_usage_left,
        email_verified: emailVerified,
      });
    }
  } catch (error) {
    console.error('Error updating Intercom user data:', error);
  }
};

/**
 *
 * @param {*string} pathname
 * @param {*string} search
 * @returns string
 *
 * This function is used to get the page name for analytics
 *
 */
const pathName = (pathname, search) => {
  let path = pathname.split("/")[1];
  if (path === "auth") {
    path = pathname.split("/")[2];
  }
  const routeName = routesMap[path] || "page view";
  if (typeof routeName === "object") {
    const routeSubName = routeName[search];
    return routeSubName;
  }
  return routeName;
};

/**
 * @param {*string} path
 * @param {*string} search
 * This function is used to track the page view
 * and send the page name to analytics
 *
 */
export const analyticsUpdate = (path, search) => {
  const analyticsPageName = pathName(path, search);
  analytics.track(analyticsPageName, {
    "content type": "page view",
    url: path + search,
  });
};
